import { MaybeRefOrGetter, computed, toValue } from 'vue'

import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/vue-query'

import { Pagination } from '../../common/models/pagination'
import { ToastEvent } from '../../common/models/toastEvent'
import { type SiteID } from '../../sites/models/site'
import { ShiftPreset, ShiftPresetID } from '../models/shiftPreset'

import { useEventStore } from '../../common/eventStore'
import { fetchAllPages } from '../../shared/api/utils/pagination'

import {
  ShiftPresetCreateForm,
  ShiftPresetSearchForm,
  ShiftPresetUpdateForm,
  create,
  fetch,
  update,
} from '../shiftPresetService'

interface ShiftPresetFilters {
  name?: MaybeRefOrGetter<string>
  perPage?: MaybeRefOrGetter<number>
  page?: MaybeRefOrGetter<number>
  /** Fetch every page and return them as one list */
  allPages?: MaybeRefOrGetter<boolean>
}

export const shiftPresetKeys = {
  all: ['shift-presets'] as const,
  lists: () => [...shiftPresetKeys.all, 'list'] as const,
  list: (
    siteId: MaybeRefOrGetter<SiteID | undefined>,
    filters: ShiftPresetFilters,
  ) =>
    [
      ...shiftPresetKeys.lists(),
      siteId,
      {
        name: filters.name,
        perPage: filters.perPage,
        page: filters.page,
        allPages: filters.allPages,
      },
    ] as const,
}

const filtersToForm = (filters: ShiftPresetFilters): ShiftPresetSearchForm => {
  return {
    name: toValue(filters.name),
    perPage: toValue(filters.perPage ?? 15),
    page: toValue(filters.page ?? 1),
  }
}

export function shiftPresetQueryOptions(
  siteId: MaybeRefOrGetter<SiteID | undefined>,
  filters: ShiftPresetFilters,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  const queryFn = computed(() => {
    const siteIdValue = toValue(siteId)
    return siteIdValue
      ? async () => {
          const form = filtersToForm(filters)

          if (!toValue(filters.allPages)) {
            return await fetch(siteIdValue, form)
          }

          const { items, pagination } = await fetchAllPages(async (page) => {
            const result = await fetch(siteIdValue, { ...form, page })

            return { items: result.shiftPresets, pagination: result.pagination }
          })

          return { shiftPresets: items, pagination }
        }
      : skipToken
  })
  return queryOptions<
    { shiftPresets: ShiftPreset[]; pagination: Pagination },
    Error
  >({
    queryKey: shiftPresetKeys.list(siteId, filters),
    queryFn: queryFn,
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  })
}

export function useShiftPresetQuery(
  siteId: MaybeRefOrGetter<SiteID | undefined>,
  filters: ShiftPresetFilters = {},
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  return useQuery(shiftPresetQueryOptions(siteId, filters, options))
}

export function useShiftPresetQueryInfinite(
  siteId: MaybeRefOrGetter<SiteID | undefined>,
  filters: ShiftPresetFilters = {},
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  const queryFn = computed(() => {
    const siteIdValue = toValue(siteId)
    return siteIdValue
      ? ({ pageParam }: { pageParam?: number }) => {
          const form = filtersToForm(filters)

          form.perPage = toValue(filters.perPage) ?? 15
          form.page = pageParam ?? 1

          return fetch(siteIdValue, form)
        }
      : skipToken
  })
  return useInfiniteQuery({
    queryKey: shiftPresetKeys.list(siteId, filters),
    queryFn: queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.pagination.currentPage + 1

      if (nextPage > lastPage.pagination.totalPages) {
        return undefined
      }

      return nextPage
    },
    getPreviousPageParam: (firstPage) => {
      const previousPage = firstPage.pagination.currentPage - 1

      if (previousPage < 1) {
        return undefined
      }

      return previousPage
    },
    enabled: options.enabled,
  })
}

export function useShiftPresetCreateMutation(siteId: MaybeRefOrGetter<SiteID>) {
  const eventStore = useEventStore()

  return useMutation<
    { shiftPreset: ShiftPreset },
    Error,
    { form: ShiftPresetCreateForm }
  >({
    mutationFn: async (shiftPresetCreate) => {
      return create(toValue(siteId), shiftPresetCreate.form)
    },
    onSuccess: async () => {
      const event = new ToastEvent('Saved')
      eventStore.addEvent(event)
    },
    // The inline form has nowhere else to say an add failed (e.g. a preset with the same times exists)
    onError: (error) => {
      eventStore.addErrorToast({ detail: error.message })
    },
  })
}

export function useShiftPresetUpdateMutation(siteId: MaybeRefOrGetter<SiteID>) {
  const eventStore = useEventStore()

  return useMutation<
    { shiftPreset: ShiftPreset },
    Error,
    { shiftPresetId: ShiftPresetID; form: ShiftPresetUpdateForm }
  >({
    mutationFn: async (shiftPresetUpdate) => {
      return update(
        toValue(siteId),
        toValue(shiftPresetUpdate.shiftPresetId),
        shiftPresetUpdate.form,
      )
    },
    onSuccess: async () => {
      const event = new ToastEvent('Saved')
      eventStore.addEvent(event)
    },
    onError: (error) => {
      eventStore.addErrorToast({ detail: error.message })
    },
  })
}
